// /memberships/:id/edit — Edit Membership Plan
//
// Reuses MembershipPlanForm + MembershipPreviewCard from the create page.
// tier_level is locked — changing it mid-lifecycle would break existing
// enrollments. Admins who need a different tier should duplicate the plan
// from the Detail page instead.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  PageContainer,
  ErrorBoundary,
  Button,
  Alert,
  Skeleton,
  useToast,
} from '@bbpos/ui';
import { ApiError } from '@/lib/api-client';
import {
  useMembershipPlan,
  useUpdateMembershipPlan,
  MembershipPreviewCard,
} from '@/features/memberships';
import type { MembershipPlan } from '@/features/memberships';
import {
  MembershipPlanForm,
  validateMembershipForm,
  INITIAL_FORM_STATE,
  type MembershipFormErrors,
  type MembershipFormState,
} from './MembershipPlanForm';
import { getSubunitMultiplier } from '@bbpos/utils';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';

function MembershipPlanEditContent({ id }: { id: string }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const planQ = useMembershipPlan(id);
  const update = useUpdateMembershipPlan(id);

  const [form, setForm] = useState<MembershipFormState>(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState<MembershipFormErrors>({});

  // Redirect to list with toast if plan not found (after loading completes).
  // Must run BEFORE any early returns so hook order stays stable across renders.
  const hasRedirected = useRef(false);
  useEffect(() => {
    if (!planQ.isLoading && !planQ.data && !hasRedirected.current) {
      hasRedirected.current = true;
      toast({ variant: 'warning', title: 'Plan not found' });
      navigate('/memberships', { replace: true });
    }
  }, [planQ.isLoading, planQ.data, navigate, toast]);

  // Hydrate form when the plan loads.
  useEffect(() => {
    if (planQ.data) {
      setForm({
        name: planQ.data.name,
        tier_level: planQ.data.tier_level,
        description: planQ.data.description ?? '',
        fee_rupees: (planQ.data.fee / getSubunitMultiplier(currency)).toString(),
        duration_days: planQ.data.duration_days.toString(),
        discount_percentage: planQ.data.discount_percentage.toString(),
        points_multiplier: planQ.data.points_multiplier.toString(),
        priority_service: planQ.data.priority_service,
        auto_renewal: planQ.data.auto_renewal,
        is_active: planQ.data.is_active,
      });
    }
  }, [planQ.data]);

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    const result = validateMembershipForm(form, currency);
    setErrors(result.errors);
    if (!result.ok || !result.payload) return;
    try {
      // tier_level is stripped from the update payload — see the CreatePage
      // comment. The rest of the fields flow through unchanged.
      const { tier_level: _tierLevel, ...rest } = result.payload;
      void _tierLevel;
      const updated = await update.mutateAsync(rest);
      toast({
        variant: 'success',
        title: 'Plan updated',
        description: `${updated.name} has been saved.`,
      });
      navigate(`/memberships/${id}`);
    } catch (err) {
      if (err instanceof ApiError) {
        const msg = err.message || '';
        if (/name|unique|duplicate|already exists/i.test(msg)) {
          setErrors((prev) => ({
            ...prev,
            name: 'Another plan already uses this name.',
          }));
          return;
        }
        toast({ variant: 'error', title: 'Could not save', description: msg });
        return;
      }
      toast({ variant: 'error', title: 'Could not save', description: 'Please try again.' });
    }
  }

  // Keyboard shortcuts — same pattern as CreatePage.
  const submitRef = useRef<() => Promise<void>>(() => Promise.resolve());
  submitRef.current = () => handleSubmit();
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        void submitRef.current();
      } else if (e.key === 'Escape') {
        navigate(`/memberships/${id}`);
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate, id]);

  const previewPlan = useMemo<Partial<MembershipPlan> & { tier_level: MembershipPlan['tier_level'] }>(
    () => ({
      name: form.name,
      tier_level: form.tier_level,
      description: form.description,
      fee: Math.max(0, Math.round(Number(form.fee_rupees) * getSubunitMultiplier(currency)) || 0),
      duration_days: Math.max(0, Math.floor(Number(form.duration_days) || 0)),
      discount_percentage: Math.max(0, Number(form.discount_percentage) || 0),
      points_multiplier: Math.max(1, Number(form.points_multiplier) || 1),
      priority_service: form.priority_service,
      auto_renewal: form.auto_renewal,
      is_active: form.is_active,
    }),
    [form],
  );

  if (planQ.isLoading) {
    return (
      <>
        <Skeleton variant="rect" height={180} className="rounded-2xl mb-6" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <Skeleton variant="rect" height={500} className="rounded-2xl lg:col-span-2" />
          <Skeleton variant="rect" height={400} className="rounded-2xl" />
        </div>
      </>
    );
  }

  if (!planQ.data) return null;

  return (
    <div className="animate-fade-in">
      {/* Hero — amber gradient to match the editing context */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-600 via-yellow-500 to-orange-600 p-6 sm:p-7 text-white mb-6 shadow-[0_25px_70px_-25px_rgba(245,158,11,0.7)]">
        <div className="pointer-events-none absolute -top-24 -right-10 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-10 h-48 w-48 rounded-full bg-black/15 blur-3xl" />
        <div className="relative">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider ring-1 ring-white/25 backdrop-blur-sm">
            Edit Plan
          </span>
          <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight">{planQ.data.name}</h1>
          <p className="mt-1.5 text-sm text-white/90 max-w-xl">
            Update pricing and benefits. Changes apply immediately; existing members keep their
            current benefits until their term expires.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="lg:col-span-2 rounded-2xl bg-white dark:bg-neutral-800 shadow-md p-6 sm:p-7 animate-slide-up"
        >
          {update.isError && !errors.name ? (
            <div className="mb-5">
              <Alert
                variant="danger"
                title="Could not save"
                description="Check the fields below and try again."
              />
            </div>
          ) : null}

          <MembershipPlanForm
            value={form}
            onChange={setForm}
            errors={errors}
            tierLocked
            footer={
              <div>
                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                  <Button
                    variant="ghost"
                    type="button"
                    onClick={() => navigate(`/memberships/${id}`)}
                  >
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit" loading={update.isPending}>
                    Save Changes
                  </Button>
                </div>
                <p className="mt-3 text-xs text-neutral-400 dark:text-neutral-500 text-right">
                  Press{' '}
                  <kbd className="rounded bg-neutral-100 dark:bg-neutral-700 px-1.5 py-0.5 font-mono text-[10px]">
                    Ctrl+Enter
                  </kbd>{' '}
                  to save ·{' '}
                  <kbd className="rounded bg-neutral-100 dark:bg-neutral-700 px-1.5 py-0.5 font-mono text-[10px]">
                    Esc
                  </kbd>{' '}
                  to cancel
                </p>
              </div>
            }
          />
        </form>

        {/* Live preview */}
        <aside className="lg:col-span-1 animate-slide-up" style={{ animationDelay: '60ms' }}>
          <div className="sticky top-4">
            <p className="mb-3 text-[10px] font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
              Preview — updated as you type
            </p>
            <MembershipPreviewCard plan={previewPlan} variant="preview" />
          </div>
        </aside>
      </div>
    </div>
  );
}

export function MembershipPlanEditPage() {
  const { id } = useParams<{ id: string }>();
  if (!id) {
    return (
      <PageContainer
        title="Edit Plan"
        breadcrumbs={[{ label: 'Memberships', href: '/memberships' }]}
      >
        <div className="p-6 text-neutral-500">Missing plan id.</div>
      </PageContainer>
    );
  }
  return (
    <PageContainer
      title="Edit Membership Plan"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Memberships', href: '/memberships' },
        { label: 'Edit' },
      ]}
    >
      <ErrorBoundary resetKey={`membership-edit-${id}`}>
        <MembershipPlanEditContent id={id} />
      </ErrorBoundary>
    </PageContainer>
  );
}
